import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import QRCode from "qrcode";
import "./styles/ServiceEditPage.css";
import useServicesService from "../../services/useServicesService";
import useLocationsService, { Location } from "../../services/useLocationsService";
import usePassengerService from "../../services/usePassengerService ";
import showToast from "../../utils/toast";
import { formatDateDDMMYYYY } from "../../utils/formatDate";
import { ServiceEdit, ServiceEditApiPayload } from "../../models/ServiceModel";
import ServiceEditModal from "../../components/Services/forms/ServiceEditModal";
import { ServicePriority, ServiceStatus, ServiceType } from "../../enums/ServiceEnum";
import BaseAlert from "../../components/Base/BaseAlert";
import BaseSelect from "../../components/Base/BaseSelect";
import RouteStopsEditor, {
  RouteStop,
  RouteStopDraft,
  stopsToDrafts,
} from "../../components/Services/forms/RouteStopsEditor";
import ServiceNotesHistoryTabs from "../../components/Services/ServiceNotesHistoryTabs";
import { isAdicionalEnabledForCompany, isEnaexCompany } from "../../config/adicionalCompany";

const TYPE_LABEL: Record<ServiceType, string> = {
  [ServiceType.DESTINO_DOMICILIOS]: "Destino → Domicilio",
  [ServiceType.ORIGEN_DOMICILIOS]: "Domicilio → Destino",
  [ServiceType.MANUAL]: "Manual",
};

/** En local, permite probar la UI de boleta sin tener el servicio en COMPLETADO (ver .env.example). */
const DEV_RELAX_BOLETA =
  process.env.NODE_ENV === "development" &&
  process.env.REACT_APP_DEV_RELAX_BOLETA_RULES === "true";

const normalizeUiError = (value: any): string => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(normalizeUiError).filter(Boolean).join(" ");
  if (value && typeof value === "object") {
    const first = Object.values(value).map(normalizeUiError).find(Boolean);
    return first || "Error en la petición";
  }
  return "Error en la petición";
};

const splitPassengerDisplayName = (fullName?: string | null) => {
  const raw = String(fullName ?? "").trim();
  if (!raw) {
    return { name: "", first_lastname: "", second_lastname: "" };
  }
  const parts = raw.split(/\s+/);
  if (parts.length === 1) {
    return { name: parts[0], first_lastname: "", second_lastname: "" };
  }
  if (parts.length === 2) {
    return { name: parts[0], first_lastname: parts[1], second_lastname: "" };
  }
  return {
    name: parts[0],
    first_lastname: parts[1],
    second_lastname: parts.slice(2).join(" "),
  };
};

const formatPassengerDisplayName = (p: any) =>
  [p?.name, p?.first_lastname, p?.second_lastname]
    .map((x) => String(x ?? "").trim())
    .filter(Boolean)
    .join(" ");

const normalizePassengerFields = (p: any) => {
  const parsed = splitPassengerDisplayName(p?.name);
  const explicitFirst = String(p?.first_lastname ?? "").trim();
  const explicitSecond = String(p?.second_lastname ?? "").trim();

  // Si backend ya trae apellidos separados, forzamos nombre base (primer token)
  // para evitar que aparezca "nombre + apellidos" dentro del campo nombre.
  if (explicitFirst || explicitSecond) {
    const rawName = String(p?.name ?? "").trim();
    const baseName = rawName ? rawName.split(/\s+/)[0] : parsed.name;
    return {
      name: baseName,
      first_lastname: explicitFirst,
      second_lastname: explicitSecond,
    };
  }

  return parsed;
};

const ServiceEditPage: React.FC = () => {
  const { id = "0" } = useParams();
  const navigate = useNavigate();
  const { edit, updateRoute, generateBoletaFromService, persistBoleta, fetching } = useServicesService();
  const { list: listLocations } = useLocationsService();
  const { updateFromService } = usePassengerService();
  
  // ✅ Estados para las alertas
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertType, setAlertType] = useState<"info" | "success" | "warning" | "error">("info");
  const [fieldError, setFieldError] = useState<string | null>(null);

  const [showEdit, setShowEdit] = useState(false);
  const [showRouteEdit, setShowRouteEdit] = useState(false);
  const [routeInvert, setRouteInvert] = useState(false);
  const [routeOrigin, setRouteOrigin] = useState("");
  const [routeDestination, setRouteDestination] = useState("");
  const [routeComment, setRouteComment] = useState("");
  const [routeStops, setRouteStops] = useState<RouteStopDraft[]>([]);
  const [savedStops, setSavedStops] = useState<RouteStop[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [editInitialSection, setEditInitialSection] = useState<"general" | "assignments" | "passengers" | undefined>(undefined);
  const [editPassengerId, setEditPassengerId] = useState<number | null>(null);
  const [quickPassengerIndex, setQuickPassengerIndex] = useState<number | null>(null);
  const [quickPassengerName, setQuickPassengerName] = useState("");
  const [quickPassengerFirstLastname, setQuickPassengerFirstLastname] = useState("");
  const [quickPassengerSecondLastname, setQuickPassengerSecondLastname] = useState("");
  const [quickPassengerPhone, setQuickPassengerPhone] = useState("");
  const [quickPassengerEmail, setQuickPassengerEmail] = useState("");
  const [quickPassengerAddress, setQuickPassengerAddress] = useState("");
  const [quickPassengerError, setQuickPassengerError] = useState<string | null>(null);
  /** Última respuesta de `edit` para el modal (evita un segundo GET al abrir). */
  const [editApiPayload, setEditApiPayload] = useState<ServiceEditApiPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [boletaQr, setBoletaQr] = useState<{
    qrUrl: string;
    token: string;
    issuedAt: string;
    expiresAt: string;
    serviceId: string;
  } | null>(null);
  const [qrImageDataUrl, setQrImageDataUrl] = useState<string | null>(null);
  const [showQrModal, setShowQrModal] = useState(false);

  const [service, setService] = useState<ServiceEdit>({
    id: 0,
    company: 0,
    company_name: "",
    service_type: ServiceType.MANUAL,
    priority: ServicePriority.NORMAL,
    style_color: null,
    service_date: "",
    service_time: "",
    origin: null,
    origin_name: null,
    destination: null,
    destination_name: null,
    driver_name: null,
    vehicle_name: null,
    authorizer_name: null,
    requester_name: null,
    cost_center_name: null,
    // vehicle_name: null,
    description: "",
    status: ServiceStatus.PREPARACION,
    driver: null,
    vehicle: null,
    passengers: [],
    form_options: { drivers: [], vehicles: [] },
    management: null,
    superintendency: null,
    area: null,
    service_reason: null,
    service_reason_text: null,
    vale_pdf_url: null,
    vale_signed_at: null,
    vale_signed_passenger_id: null,
    enaex_code: null as string | null,
  });

  const FINAL_STATUSES = [ServiceStatus.COMPLETADO, ServiceStatus.ANULADO, ServiceStatus.CANCELADO];
  const isEditable = !FINAL_STATUSES.includes(service.status);
  const isBoletaEnabled =
    service.status === ServiceStatus.COMPLETADO || DEV_RELAX_BOLETA;

  // 🔹 Función central para recargar el servicio
  const ReloadService = useCallback(() => {
    setLoading(true);
    edit(Number(id), {
      onSuccess: (resp) => {
        const responseData = resp?.data ?? {};

        if (responseData.errors) {
          setAlertType("error");
          setAlertMessage(normalizeUiError(responseData.errors));
          setEditApiPayload(null);
          setLoading(false);
          return;
        }
        setEditApiPayload(responseData as ServiceEditApiPayload);
        setSavedStops(Array.isArray(responseData.stops) ? responseData.stops : []);
        const serviceData = responseData.service ?? {};
        let passengers = responseData.passengers ?? [];

        if (typeof passengers === "string") {
          try {
            passengers = JSON.parse(passengers);
          } catch {
            passengers = [];
          }
        }

        setService({
          id: serviceData.id ?? 0,
          company: serviceData.company ?? 0,
          company_name: serviceData.company_name ?? "",
          service_type:
            (ServiceType[serviceData.service_type as keyof typeof ServiceType] ??
              ServiceType.MANUAL),
          priority:
            serviceData.priority === ServicePriority.VIP
              ? ServicePriority.VIP
              : ServicePriority.NORMAL,
          style_color:
            serviceData.style_color === "purple" || serviceData.style_color === "orange"
              ? serviceData.style_color
              : null,
          service_date: serviceData.service_date ?? "",
          service_time: serviceData.service_time ?? "",
          description: serviceData.description ?? "",
          origin: serviceData.origin ?? null,
          origin_name: serviceData.origin_name ?? null,
          destination: serviceData.destination ?? null,
          destination_name: serviceData.destination_name ?? null,
          driver_name: serviceData.driver_name ?? null,
          vehicle_name: serviceData.vehicle_name ?? null,
          authorizer_name: serviceData.authorizer_name ?? null,
          requester_name: serviceData.requester_name ?? null,
          cost_center_name: serviceData.cost_center_name ?? null,
          driver: serviceData.driver ?? null,
          vehicle: serviceData.vehicle ?? null,
          authorizer: serviceData.authorizer ?? null,
          requester: serviceData.requester ?? null,
          status:
            Object.values(ServiceStatus).includes(serviceData.status as ServiceStatus)
              ? (serviceData.status as ServiceStatus)
              : ServiceStatus.PREPARACION,
          passengers: Array.isArray(responseData.passengers)
            ? responseData.passengers.map((p: any) => ({
                id: p.id ?? null,
                name: normalizePassengerFields(p).name ?? null,
                first_lastname: normalizePassengerFields(p).first_lastname ?? null,
                second_lastname: normalizePassengerFields(p).second_lastname ?? null,
                phone: p.phone ?? null,
                email: p.email ?? null,
                address: p.address ?? null,
                address_id: p.address_id ?? null,
                order: p.order ?? null,
              }))
            : [],
          form_options: {
            drivers: responseData.form_options?.drivers ?? [],
            vehicles: responseData.form_options?.vehicles ?? [],
            assignment_selects: responseData.form_options?.assignment_selects,
          },
          management: serviceData.management ?? null,
          superintendency: serviceData.superintendency ?? null,
          area: serviceData.area ?? null,
          service_reason: serviceData.service_reason ?? null,
          service_reason_text: serviceData.service_reason_text ?? null,
          vale_pdf_url: serviceData.vale_pdf_url ?? null,
          vale_signed_at: serviceData.vale_signed_at ?? null,
          vale_signed_passenger_id: serviceData.vale_signed_passenger_id ?? null,
          enaex_code: serviceData.enaex_code ?? null,
        });

        setLoading(false);
      },
      onError: () => {
        showToast("Error cargando servicio", "error");
        setLoading(false);
      },
    });
  }, [id, edit]);

  const passengersWithSeq = useMemo(() => {
    const list = Array.isArray(service?.passengers)
      ? service.passengers
      : [];
    return list.map((p, i) => ({ ...p, seq: i + 1 }));
  }, [service]);

  const openQuickPassengerModal = useCallback((index: number) => {
    const selected = passengersWithSeq[index];
    if (!selected) return;
    const parsed = normalizePassengerFields(selected);
    setQuickPassengerIndex(index);
    setQuickPassengerName(parsed.name ?? "");
    setQuickPassengerFirstLastname(parsed.first_lastname ?? "");
    setQuickPassengerSecondLastname(parsed.second_lastname ?? "");
    setQuickPassengerPhone(selected.phone ?? "");
    setQuickPassengerEmail(selected.email ?? "");
    setQuickPassengerAddress(selected.address ?? "");
    setQuickPassengerError(null);
  }, [passengersWithSeq]);

  const closeQuickPassengerModal = useCallback(() => {
    setQuickPassengerIndex(null);
    setQuickPassengerError(null);
  }, []);

  const saveQuickPassengerModal = useCallback(async () => {
    if (quickPassengerIndex == null) return;
    if (!quickPassengerName.trim()) {
      setQuickPassengerError("El nombre del pasajero es obligatorio.");
      return;
    }
    if (!quickPassengerAddress.trim()) {
      setQuickPassengerError("La dirección del pasajero es obligatoria.");
      return;
    }

    const selectedPassenger = passengersWithSeq[quickPassengerIndex];
    if (!selectedPassenger?.id) {
      setQuickPassengerError("No se pudo identificar el pasajero para actualizar.");
      return;
    }
    if (!selectedPassenger?.address_id) {
      setQuickPassengerError(
        "La dirección actual no tiene identificador. Recarga la página para sincronizar."
      );
      return;
    }

    const response = await updateFromService(Number(selectedPassenger.id), {
      name: quickPassengerName.trim(),
      first_lastname: quickPassengerFirstLastname.trim() || null,
      second_lastname: quickPassengerSecondLastname.trim() || null,
      phone: quickPassengerPhone.trim() || null,
      email: quickPassengerEmail.trim() || null,
      address: quickPassengerAddress.trim(),
      address_id: selectedPassenger.address_id ?? null,
    });

    if (!response || response.success === false || (response.http_status ?? 0) >= 400) {
      setQuickPassengerError(
        normalizeUiError(response?.errors ?? response?.message ?? "No se pudo actualizar pasajero.")
      );
      return;
    }

    const updatedAddressId = response?.data?.address?.id ?? selectedPassenger.address_id ?? null;
    const updatedAddressStreet = response?.data?.address?.street ?? quickPassengerAddress.trim();

    setService((prev) => {
      const current = Array.isArray(prev.passengers) ? prev.passengers : [];
      const updatedPassengers = current.map((p: any, i: number) =>
        i === quickPassengerIndex
          ? {
              ...p,
              name: quickPassengerName.trim(),
              first_lastname: quickPassengerFirstLastname.trim(),
              second_lastname: quickPassengerSecondLastname.trim(),
              phone: quickPassengerPhone.trim(),
              email: quickPassengerEmail.trim(),
              address: updatedAddressStreet,
              address_id: updatedAddressId,
            }
          : p
      );
      return { ...prev, passengers: updatedPassengers };
    });
    showToast("Pasajero actualizado", "success");
    closeQuickPassengerModal();
  }, [
    closeQuickPassengerModal,
    passengersWithSeq,
    quickPassengerAddress,
    quickPassengerEmail,
    quickPassengerFirstLastname,
    quickPassengerIndex,
    quickPassengerName,
    quickPassengerPhone,
    quickPassengerSecondLastname,
    updateFromService,
  ]);

  // ✅ Handler cuando el modal actualiza correctamente
  const handleEditSuccess = useCallback(() => {
    setShowEdit(false);
    setEditInitialSection(undefined);
    setEditPassengerId(null);
    setAlertType("success");
    setAlertMessage("Servicio actualizado correctamente");
    setFieldError(null);
    ReloadService();
    
    // Auto-ocultar después de 3 segundos
    setTimeout(() => setAlertMessage(""), 3000);
  }, [ReloadService]);

  // ✅ Handler cuando el modal tiene un error
  const handleEditError = useCallback((error: any) => {
    const message = normalizeUiError(error);
    setAlertType("error");
    setAlertMessage(message);
    setFieldError(message);
    // NO cerrar el modal - dejar que el usuario corrija
  }, []);

  // ✅ Handler para cerrar el modal
  const handleCloseModal = useCallback(() => {
    setShowEdit(false);
    setEditInitialSection(undefined);
    setEditPassengerId(null);
    setFieldError(null);
    setAlertMessage("");
  }, []);

  useEffect(() => {
    ReloadService();
  }, [ReloadService]);

  useEffect(() => {
    if (!boletaQr?.qrUrl) {
      setQrImageDataUrl(null);
      return;
    }

    QRCode.toDataURL(boletaQr.qrUrl, { margin: 1, width: 220 })
      .then(setQrImageDataUrl)
      .catch(() => {
        setQrImageDataUrl(null);
        showToast("No se pudo renderizar el QR", "error");
      });
  }, [boletaQr]);

  const handleGenerateBoleta = useCallback(() => {
    if (!isBoletaEnabled) {
      showToast("La boleta solo se puede generar cuando el servicio está COMPLETADO.", "info");
      return;
    }

    generateBoletaFromService(service.id, {
      onSuccess: (resp) => {
        const payload = resp?.data;
        if (!payload?.qrUrl) {
          showToast("Respuesta inválida al generar boleta", "error");
          return;
        }
        setBoletaQr(payload);
        showToast("Boleta generada con QR correctamente", "success");
      },
      onError: (err) => {
        showToast(err?.message || "No fue posible generar boleta", "error");
      },
    });
  }, [generateBoletaFromService, isBoletaEnabled, service.id]);

  const handlePersistStub = useCallback(() => {
    if (!boletaQr) {
      showToast("Primero debes generar la boleta", "info");
      return;
    }

    persistBoleta(service.id, { serviceId: boletaQr.serviceId }, {
      onSuccess: () => showToast("Boleta persistida correctamente", "success"),
      onError: (err) => {
        showToast(err?.message || "Persistencia no disponible", "error");
      },
    });
  }, [boletaQr, persistBoleta, service.id]);

  const openRouteEditor = (invert = false) => {
    const origin = String(service.origin ?? "");
    const destination = String(service.destination ?? "");
    setRouteInvert(invert);
    setRouteOrigin(invert ? destination : origin);
    setRouteDestination(invert ? origin : destination);
    setRouteComment("");
    const drafts = stopsToDrafts(savedStops);
    setRouteStops(invert ? drafts.reverse() : drafts);
    listLocations({ all: true }, {
      onSuccess: (resp) => {
        const data = resp?.data;
        const items = Array.isArray(data) ? data : data?.locations ?? [];
        setLocations(Array.isArray(items) ? items : []);
      },
      onError: () => showToast("No se pudieron cargar las localizaciones", "error"),
    });
    setShowRouteEdit(true);
  };

  const swapRouteEndpoints = () => {
    const nextOrigin = routeDestination;
    const nextDestination = routeOrigin;
    setRouteOrigin(nextOrigin);
    setRouteDestination(nextDestination);
    setRouteStops((prev) => [...prev].reverse());
    const originalOrigin = String(service.origin ?? "");
    const originalDestination = String(service.destination ?? "");
    setRouteInvert(
      nextOrigin === originalDestination && nextDestination === originalOrigin
    );
  };

  const saveRoute = () => {
    if (!routeComment.trim()) {
      showToast("Debes ingresar un comentario para modificar la ruta.", "error");
      return;
    }
    const payload: { origin?: number; destination?: number; comment: string; invert?: boolean; stops: number[] } = {
      comment: routeComment.trim(),
      invert: routeInvert,
      stops: routeStops.filter((s) => s.location).map((s) => Number(s.location)),
    };
    if (!routeInvert) {
      if (!routeOrigin || !routeDestination) {
        showToast("Debes seleccionar origen y destino.", "error");
        return;
      }
      payload.origin = Number(routeOrigin);
      payload.destination = Number(routeDestination);
    }
    updateRoute(service.id, payload, {
      onSuccess: () => {
        setShowRouteEdit(false);
        showToast(routeInvert ? "Recorrido invertido correctamente" : "Ruta actualizada correctamente", "success");
        ReloadService();
      },
      onError: (resp) => showToast(normalizeUiError(resp?.message ?? resp?.data), "error"),
    });
  };

  if (loading || !service) {
    return (
      <div className="sp-edit">
        <div className="sp-header">
          <button className="sp-btn ghost" onClick={() => navigate(-1)}>
            ← Volver
          </button>
          <h1>Cargando servicio…</h1>
        </div>
      </div>
    );
  }

return (
    <div className="sp-edit-pro">

      {/* ✅ Alerta de éxito/error */}
      {alertMessage && (
        <BaseAlert
          type={alertType}
          message={alertMessage}
          onClose={() => {
            setAlertMessage("");
            setFieldError(null);
          }}
        />
      )}

      {/* 🎨 HEADER MEJORADO */}
      <div className="service-edit-header-pro">
        <div className="header-top-pro">
          <button className="btn-back-pro" onClick={() => navigate(-1)}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Volver</span>
          </button>
          
          <div className="header-center-pro">
            <h1 className="service-title-pro">Servicio {service.id}</h1>
            <p className="service-meta-pro">
              {TYPE_LABEL[service.service_type]} • {service.company_name || "—"}
              {service.priority === ServicePriority.VIP ? " • VIP" : ""}
            </p>
          </div>

          <div className="header-actions-pro">
            {/*isBoletaEnabled && (
              <button
                className="btn-edit-pro btn-boleta-pro"
                onClick={boletaQr ? () => setShowQrModal(true) : handleGenerateBoleta}
                disabled={Boolean(fetching.generateBoletaFromService)}
                title={boletaQr ? "Ver QR de boleta" : "Generar boleta con validación QR"}
              >
                <span>
                  {boletaQr
                    ? "Ver QR + link"
                    : fetching.generateBoletaFromService
                    ? "Generando..."
                    : "Generar boleta"}
                </span>
              </button>
            )*/}
            {/* 🛑 APLICAR LA CONDICIÓN AQUÍ: Solo renderizar el botón si isEditable es true */}
            {isEditable && (
              <button
                className="btn-edit-pro"
                onClick={() => {
                  setEditInitialSection(undefined);
                  setEditPassengerId(null);
                  setShowEdit(true);
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Editar</span>
              </button>
            )}
            {/* El estado siempre se muestra */}
            <span className={`status-badge-pro status-${service.status.toLowerCase()}`}>
              {service.status}
            </span>
          </div>
        </div>

        {/* Modal */}
        {showEdit && isEditable && (
          <ServiceEditModal
            passengers={service.passengers}
            serviceId={service.id}
            initialEditData={editApiPayload}
            initialSection={editInitialSection}
            initialPassengerId={editPassengerId}
            onClose={handleCloseModal}
            onUpdated={handleEditSuccess}
            onError={handleEditError}
            fieldError={fieldError}
            onAddSequence ={(seq) => setService(prev => ({ ...prev, sequences: seq }))}
          />
        )}
      </div>

      {/*{DEV_RELAX_BOLETA && service.status !== ServiceStatus.COMPLETADO && (
        <p className="boleta-info" style={{ marginBottom: 12 }}>
          Modo dev: puedes usar la boleta en UI; si el backend exige COMPLETADO, la API responderá error de negocio.
        </p>
      )*/}

      <div className="sp-layout-pro">

        <section className="sp-card-pro sp-summary-card" aria-labelledby="sp-summary-title">
          <div className="sp-card-header-pro">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
              <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <h3 id="sp-summary-title" className="sp-card-title-pro">Resumen</h3>
          </div>

          <dl className="sp-summary-row">
            <div className="sp-summary-col">
              <dt className="sp-summary-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2"/>
                  <polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Empresa
              </dt>
              <dd className="sp-summary-value" title={service.company_name || undefined}>
                {service.company_name || "—"}
              </dd>
            </div>

            <div className="sp-summary-col">
              <dt className="sp-summary-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                  <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                  <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Fecha
              </dt>
              <dd className="sp-summary-value">
                {formatDateDDMMYYYY(service.service_date) || "—"}
              </dd>
            </div>

            <div className="sp-summary-col">
              <dt className="sp-summary-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Hora
              </dt>
              <dd className="sp-summary-value">{service.service_time || "—"}</dd>
            </div>

            <div className="sp-summary-col">
              <dt className="sp-summary-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Conductor
              </dt>
              <dd className="sp-summary-value" title={service.driver_name || undefined}>
                {service.driver_name || "—"}
              </dd>
            </div>

            <div className="sp-summary-col">
              <dt className="sp-summary-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M5 17h14v2H5v-2zm0-4h14v2H5v-2zm1-7l1.5-2h9l1.5 2H5zm11.5 4.5L16 8H8l-.5 2.5 1 6 1.5 1h4l1.5-1 1-6z" fill="currentColor"/>
                </svg>
                Vehículo
              </dt>
              <dd className="sp-summary-value" title={service.vehicle_name || undefined}>
                {service.vehicle_name || "—"}
              </dd>
            </div>

            <div className="sp-summary-col sp-summary-col--person">
              <dt className="sp-summary-label">Autorizador</dt>
              <dd className="sp-summary-pill sp-pill-primary" title={service.authorizer_name || undefined}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <span>{service.authorizer_name || "—"}</span>
              </dd>
            </div>

            <div className="sp-summary-col sp-summary-col--person">
              <dt className="sp-summary-label">Solicitante</dt>
              <dd className="sp-summary-pill sp-pill-primary" title={service.requester_name || undefined}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <span>{service.requester_name || "—"}</span>
              </dd>
            </div>

            <div className="sp-summary-col sp-summary-col--person">
              <dt className="sp-summary-label">Centro de costo</dt>
              <dd className="sp-summary-pill sp-pill-secondary" title={service.cost_center_name || undefined}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <span>{service.cost_center_name || "—"}</span>
              </dd>
            </div>
          </dl>
        </section>

        <div className="sp-detail-grid">

          {/* CARD PASAJEROS */}
          <div className="sp-card-pro sp-card-table">
            <div className="sp-card-header-pro">
              <div className="sp-card-title-group">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <h3 className="sp-card-title-pro">Pasajeros y direcciones</h3>
              </div>
              <span className="sp-count-badge-pro">{passengersWithSeq.length}</span>
            </div>
            
            <div className="sp-table-wrapper-pro">
              <table className="passengers-table">
                <thead>
                  <tr>
                    <th className="th-pro th-name">Pasajero</th>
                    <th className="th-pro th-phone">Teléfono</th>
                    <th className="th-pro th-address">Dirección</th>
                    <th className="th-pro th-seq">#</th>
                    {isEditable && <th className="th-pro th-actions">Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {passengersWithSeq.length === 0 ? (
                    <tr>
                      <td colSpan={isEditable ? 5 : 4} className="empty-state-pro">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                          <line x1="19" y1="8" x2="19" y2="14" stroke="currentColor" strokeWidth="2"/>
                          <line x1="22" y1="11" x2="16" y2="11" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        <p>Sin pasajeros asignados</p>
                      </td>
                    </tr>
                  ) : (
                    passengersWithSeq.map((p) => (
                      <tr key={p.id ?? Math.random()} className="table-row-pro">
                        <td className="td-pro td-name-pro">
                          <div className="passenger-cell">
                            <div className="passenger-avatar">
                              {formatPassengerDisplayName(p)?.charAt(0).toUpperCase()}
                            </div>
                            <span>{formatPassengerDisplayName(p) || "—"}</span>
                          </div>
                        </td>
                        <td className="td-pro td-phone-pro">
                          {p.phone?.trim() ? p.phone : "—"}
                        </td>
                        <td className="td-pro td-address-pro">
                          <div className="address-cell">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2"/>
                              <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            {p.address}
                          </div>
                        </td>
                        <td className="td-pro td-seq-pro">
                          <span className="seq-badge-pro">{p.seq}</span>
                        </td>
                        {isEditable && (
                          <td className="td-pro td-actions-pro">
                            <button
                              type="button"
                              className="sp-table-edit-btn"
                              onClick={() => {
                                openQuickPassengerModal(p.seq - 1);
                              }}
                            >
                              Editar
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* CARD LOCALIZACIONES */}
          <div className="sp-card-pro">
            <div className="sp-card-header-pro">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <h3 className="sp-card-title-pro">Ruta servicio</h3>
              {isEditable && (
                <div className="sp-route-actions">
                  <button type="button" className="sp-table-edit-btn" onClick={() => openRouteEditor(false)}>
                    Modificar
                  </button>
                </div>
              )}
            </div>

            <div className="sp-location-block-pro">
              <label className="sp-location-label-pro">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="3" fill="currentColor"/>
                </svg>
                Origen
              </label>
              <div className="sp-location-value-pro sp-location-origin">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                </svg>
                {service.origin_name}
              </div>
            </div>

            <div className="sp-location-separator-pro">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4"/>
                <polyline points="12 19 8 15" stroke="currentColor" strokeWidth="2"/>
                <polyline points="12 19 16 15" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>

            {savedStops.map((stop, index) => (
              <React.Fragment key={stop.id}>
                <div className="sp-location-block-pro">
                  <div className="sp-location-value-pro sp-location-stop">
                    <span className="sp-location-stop-index" aria-label={`Punto ${index + 1}`}>
                      {index + 1}
                    </span>
                    {stop.location_name || "—"}
                  </div>
                </div>
                <div className="sp-location-separator-pro">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4"/>
                    <polyline points="12 19 8 15" stroke="currentColor" strokeWidth="2"/>
                    <polyline points="12 19 16 15" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
              </React.Fragment>
            ))}

            <div className="sp-location-block-pro">
              <label className="sp-location-label-pro">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Destino
              </label>
              <div className="sp-location-value-pro sp-location-destination">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2"/>
                </svg>
                {service.destination_name || "—"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showRouteEdit &&
        createPortal(
          <div className="sp-quick-passenger-overlay" onClick={() => setShowRouteEdit(false)}>
            <div className="sp-quick-passenger-modal sp-route-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sp-quick-passenger-head">
                <h4>{routeInvert ? "Invertir recorrido" : "Modificar ruta"}</h4>
                <button type="button" onClick={() => setShowRouteEdit(false)} aria-label="Cerrar">×</button>
              </div>
              <div className="sp-quick-passenger-body">
                <BaseSelect
                  label="Origen"
                  name="route_origin"
                  value={routeOrigin}
                  onChange={(e) => {
                    setRouteOrigin(e.target.value);
                    setRouteInvert(false);
                  }}
                  placeholder="Seleccionar origen"
                  preserveCase
                  searchable
                  options={locations.filter(l => l.id).map(l => ({ value: String(l.id), label: l.name ?? "" }))}
                />
                <RouteStopsEditor
                  stops={routeStops}
                  onChange={setRouteStops}
                  options={locations.filter(l => l.id).map(l => ({ value: String(l.id), label: l.name ?? "" }))}
                />
                <div className="sp-route-swap-row">
                  <button
                    type="button"
                    className="sp-route-swap-btn"
                    onClick={swapRouteEndpoints}
                    title="Intercambiar origen y destino"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M7 16V4M7 4L3 8M7 4l4 4M17 8v12M17 20l4-4M17 20l-4-4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Invertir
                  </button>
                </div>
                <BaseSelect
                  label="Destino"
                  name="route_destination"
                  value={routeDestination}
                  onChange={(e) => {
                    setRouteDestination(e.target.value);
                    setRouteInvert(false);
                  }}
                  placeholder="Seleccionar destino"
                  preserveCase
                  searchable
                  options={locations.filter(l => l.id).map(l => ({ value: String(l.id), label: l.name ?? "" }))}
                />
                <label className="sp-route-comment-field">
                  Comentario obligatorio
                  <textarea
                    value={routeComment}
                    onChange={(e) => setRouteComment(e.target.value)}
                    rows={3}
                    placeholder="Describe el motivo del cambio de ruta…"
                  />
                </label>
              </div>
              <div className="sp-quick-passenger-actions">
                <button
                  type="button"
                  className="sp-quick-passenger-btn sp-quick-passenger-btn--ghost"
                  onClick={() => setShowRouteEdit(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="sp-quick-passenger-btn sp-quick-passenger-btn--primary"
                  onClick={saveRoute}
                  disabled={Boolean(fetching.updateRoute)}
                >
                  {fetching.updateRoute ? "Guardando…" : "Confirmar"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {quickPassengerIndex != null &&
        createPortal(
          <div className="sp-quick-passenger-overlay" onClick={closeQuickPassengerModal}>
            <div
              className="sp-quick-passenger-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sp-quick-passenger-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sp-quick-passenger-head">
                <h4 id="sp-quick-passenger-title">Editar pasajero</h4>
                <button type="button" onClick={closeQuickPassengerModal} aria-label="Cerrar">
                  ×
                </button>
              </div>
              <div className="sp-quick-passenger-body">
                <label>
                  Nombre
                  <input
                    value={quickPassengerName}
                    onChange={(e) => {
                      setQuickPassengerName(e.target.value);
                      setQuickPassengerError(null);
                    }}
                    autoFocus
                  />
                </label>
                <label>
                  Primer apellido
                  <input
                    value={quickPassengerFirstLastname}
                    onChange={(e) => setQuickPassengerFirstLastname(e.target.value)}
                  />
                </label>
                <label>
                  Segundo apellido
                  <input
                    value={quickPassengerSecondLastname}
                    onChange={(e) => setQuickPassengerSecondLastname(e.target.value)}
                  />
                </label>
                <label>
                  Teléfono
                  <input
                    value={quickPassengerPhone}
                    onChange={(e) => setQuickPassengerPhone(e.target.value)}
                  />
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={quickPassengerEmail}
                    onChange={(e) => setQuickPassengerEmail(e.target.value)}
                    placeholder="correo@empresa.com"
                  />
                </label>
                <label>
                  Dirección (secuencia)
                  <input
                    value={quickPassengerAddress}
                    onChange={(e) => {
                      setQuickPassengerAddress(e.target.value);
                      setQuickPassengerError(null);
                    }}
                  />
                </label>
                {quickPassengerError && (
                  <p className="sp-quick-passenger-error">{quickPassengerError}</p>
                )}
              </div>
              <div className="sp-quick-passenger-actions">
                <button
                  type="button"
                  className="sp-quick-passenger-btn sp-quick-passenger-btn--ghost"
                  onClick={closeQuickPassengerModal}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="sp-quick-passenger-btn sp-quick-passenger-btn--primary"
                  onClick={saveQuickPassengerModal}
                >
                  Guardar cambios
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Pestañas: Historial | Notas | Adicional (El Abra o ENAEX, tablas distintas en backend) */}
      <div className="sp-history-section">
        <ServiceNotesHistoryTabs
          serviceId={service.id}
          showAdicionalTab={
            isAdicionalEnabledForCompany(service.company_name) ||
            isEnaexCompany(service.company_name)
          }
          adicionalKind={
            isAdicionalEnabledForCompany(service.company_name)
              ? "el_abra"
              : isEnaexCompany(service.company_name)
                ? "enaex"
                : null
          }
          // valePdfUrl={service.vale_pdf_url}
          adicionalInitialValues={{
            management: service.management ?? null,
            superintendency: service.superintendency ?? null,
            area: service.area ?? null,
            service_reason: service.service_reason ?? null,
            service_reason_text: service.service_reason_text ?? null,
          }}
          enaexInitialCode={service.enaex_code}
          onAdicionalSaved={ReloadService}
        />
      </div>

      {/* <button className="sp-btn ghost" onClick={() => navigate(-1)}>
        ← Volver
      </button> */}

      {/* <div className="sp-footer">
        <button className="sp-btn ghost" onClick={() => navigate(-1)}>
          Cancelar
        </button>
        <button
          className="sp-btn primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Guardando…" : "Guardar cambios"}
        </button>
        {saved && <span className="saved-pop">✓ Guardado</span>}
      </div> */}

      {showQrModal && boletaQr && (
        <div className="boleta-modal-overlay" onClick={() => setShowQrModal(false)}>
          <div className="boleta-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="boleta-modal-header">
              <h4>Boleta QR</h4>
              <button type="button" className="boleta-modal-close" onClick={() => setShowQrModal(false)}>
                Cerrar
              </button>
            </div>
            <div className="boleta-modal-body">
              {qrImageDataUrl ? (
                <img src={qrImageDataUrl} alt="QR boleta" className="boleta-qr-image" />
              ) : (
                <p className="boleta-info">No se pudo renderizar el QR.</p>
              )}
              <a className="boleta-qr-link" href={boletaQr.qrUrl} target="_blank" rel="noreferrer">
                Abrir link de validacion
              </a>
              <button
                className="btn-edit-pro btn-boleta-persist-pro"
                onClick={handlePersistStub}
                disabled={Boolean(fetching.persistBoleta)}
              >
                {fetching.persistBoleta ? "Persistiendo..." : "Persistir boleta (stub)"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceEditPage;